import {FC, useState} from 'react'
import {Badge, Button, Form, Nav} from 'react-bootstrap'
import {addDays, format, startOfDay} from 'date-fns'
import {useStore} from '../../store'
import {eventDayTime, templateDayIndex} from '../../helpers/templates'
import {useTemplateMode} from './useTemplateMode'

// Weekly templates: seven day pills above the daily grid, ordered by the
// COMPANY's week start (the anchor week already begins on it) — labels come
// from the anchor dates, not a hardcoded Mon–Sun list. Tab switching is just
// a selectedDate change onto the anchor week's matching day — Timeline already
// keys all filtering off selectedDate, so no grid plumbing is needed.
const TemplateDayTabs: FC = () => {
  const {selectedDate, templateAnchorStart, events, handleState} = useStore()
  if (!templateAnchorStart) return null
  const activeIndex = templateDayIndex(templateAnchorStart, selectedDate)

  return (
    <Nav variant='pills' className='flex-nowrap'>
      {[0, 1, 2, 3, 4, 5, 6].map((i) => {
        const dayDate = addDays(templateAnchorStart, i)
        const label = format(dayDate, 'EEE')
        const dayTime = startOfDay(dayDate).getTime()
        // Same day key the grid and the row labels use — a shift dragged into
        // a past-midnight slot starts on the next date but belongs to this tab.
        const shiftCount = events.filter((ev) => eventDayTime(ev) === dayTime).length
        return (
          <Nav.Item key={label}>
            <Nav.Link
              active={i === activeIndex}
              className='py-1 px-3 fs-7'
              onClick={() => handleState(dayDate, 'selectedDate')}
            >
              {label}
              {shiftCount > 0 && (
                <Badge bg={i === activeIndex ? 'light' : 'secondary'} text='dark' className='ms-1'>
                  {shiftCount}
                </Badge>
              )}
            </Nav.Link>
          </Nav.Item>
        )
      })}
    </Nav>
  )
}

// Replaces LaborSchedulerHeader while template mode is on: template name,
// type badge, weekly day tabs, and the Add row / Save / Exit actions.
const TemplateHeader: FC = () => {
  const {templateDraft, handleState, handleMultipleStates} = useStore()
  const {saveTemplate, requestExit} = useTemplateMode()
  const [saving, setSaving] = useState(false)

  if (!templateDraft) return null

  const setName = (name: string) => {
    handleMultipleStates(
      new Map<string, any>([
        ['templateDraft', {...templateDraft, name}],
        ['templateDirty', true],
      ])
    )
  }

  const addRow = () => {
    handleState({...templateDraft, rowCount: templateDraft.rowCount + 1}, 'templateDraft')
  }

  const handleSave = async () => {
    setSaving(true)
    await saveTemplate()
    setSaving(false)
  }

  return (
    <div
      className='card mb-2 mb-xl-2'
      style={{
        position: 'sticky',
        top: 'calc(var(--bs-app-header-height) + var(--bs-app-toolbar-height))',
        zIndex: 15,
      }}
    >
      <div
        className='card-body px-8 py-3'
        style={{backgroundColor: 'var(--bs-app-toolbar-base-bg-color)'}}
      >
        <div className='d-flex flex-wrap align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-wrap align-items-center gap-3'>
            <span className='badge badge-light-primary fs-8'>Template mode</span>
            <Form.Control
              size='sm'
              type='text'
              className='w-200px'
              placeholder='Template name'
              value={templateDraft.name}
              onChange={(e) => setName(e.target.value)}
            />
            <Badge bg={templateDraft.template_type === 'weekly' ? 'info' : 'secondary'}>
              {templateDraft.template_type === 'weekly' ? 'Weekly' : 'Daily'}
            </Badge>
            {templateDraft.template_type === 'weekly' && <TemplateDayTabs />}
          </div>
          <div className='d-flex align-items-center gap-2'>
            <Button size='sm' variant='light' onClick={addRow}>
              <i className='bi bi-plus-lg me-1'></i>Add shift
            </Button>
            <Button size='sm' variant='primary' disabled={saving} onClick={handleSave}>
              {saving ? (
                <span className='indicator-progress d-inline-block'>
                  Saving…{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-1'></span>
                </span>
              ) : (
                'Save template'
              )}
            </Button>
            <Button size='sm' variant='secondary' onClick={() => requestExit()}>
              Exit
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export {TemplateHeader}
